import { TCustomThemeViews } from '@olo-web/types/merchantTheme.interface';
import { EFirestoreCollections } from './../../../types/enums/firestoreCollections.enum';
import { getMerchantThemeCollectionName } from '@olo-web/utils/common/functions/getMerchantThemeCollectionName';
import { initialState, useCustomThemeDraftDispatch } from '@olo-web/client-state';
import { FirebaseError } from 'firebase-admin';
import { firestore } from '../../../utils/google/firebase/firebase';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { useRouter } from 'next/router';
import { useMerchantIdentification } from '../queries/useMerchantIdentification';
import { useMerchantTheme } from '../queries/useMerchantTheme';

type T = {
  field: TCustomThemeViews | undefined;
  merchantIds?: string[];
};

export const revertMerchantTheme = async ({ field, merchantIds }: T): Promise<void> => {
  try {
    if (!merchantIds || merchantIds.length === 0) {
      throw new Error('No merchant IDs provided for theme revert');
    }

    const querySnapshot = await firestore()
      .collection(getMerchantThemeCollectionName())
      .where('merchantIds', 'array-contains-any', merchantIds)
      .get();

    if (querySnapshot.empty) {
      console.error('No matching theme documents found for merchantIds:', merchantIds);
      throw new Error('No matching theme documents found');
    }

    const batch = firestore().batch();
    querySnapshot.docs.forEach((doc) => {
      batch.update(doc.ref, { [field]: initialState[field] });
    });

    await batch.commit();
    return;
  } catch (error) {
    console.error('Error reverting merchant theme:', error);
    throw new Error(error?.message || error);
  }
};

export const useRevertMerchantTheme = (options?: UseMutationOptions<void, FirebaseError, T>) => {
  const queryClient = useQueryClient();
  const themeDispatch = useCustomThemeDraftDispatch();
  const router = useRouter();
  const { merchantId } = router.query;
  const { data: merchantIdentification } = useMerchantIdentification(merchantId as string);
  const { data: currentTheme } = useMerchantTheme();

  return useMutation(revertMerchantTheme, {
    ...options,
    onMutate: (variables) => {
      if (merchantIdentification) {
        const merchantIds = [];

        if (merchantIdentification.merchant_id) {
          merchantIds.push(merchantIdentification.merchant_id);
        }

        if (
          merchantIdentification.business_location_id &&
          !merchantIds.includes(merchantIdentification.business_location_id)
        ) {
          merchantIds.push(merchantIdentification.business_location_id);
        }

        if (currentTheme?.merchantIds?.length) {
          currentTheme.merchantIds.forEach((id) => {
            if (!merchantIds.includes(id)) {
              merchantIds.push(id);
            }
          });
        }

        if (merchantIds.length > 0) {
          variables = {
            ...variables,
            merchantIds,
          };
        }
      }

      if (options?.onMutate) {
        return options.onMutate(variables);
      }

      return variables;
    },
    onSuccess: (_, variables, context) => {
      queryClient.refetchQueries(EFirestoreCollections.MERCHANT_THEME);
      themeDispatch({ type: 'CLEAR_THEME_DRAFT' });
      if (options?.onSuccess) options.onSuccess(_, variables, context);
    },
  });
};
